import React from "react";

interface Player {
  playerId: string;
  displayName: string;
  characterId: string;
  characterName: string;
  characterClass: string;
  characterLevel: number;
}

interface SessionListItemProps {
  id: string;
  title: string;
  date: string;
  time?: string;
  location: string;
  maxPlayers: number;
  players: Player[];
  dmName?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  tertiaryActionLabel?: string;
  onTertiaryAction?: () => void;
}

const SessionListItem: React.FC<SessionListItemProps> = ({
  title,
  date,
  time = "",
  location,
  maxPlayers,
  players,
  dmName,
  isExpanded = false,
  onToggleExpand,
  actionLabel,
  onAction,
  actionDisabled,
  secondaryActionLabel,
  onSecondaryAction,
  tertiaryActionLabel,
  onTertiaryAction,
}) => {
  const handleItemClick = (e: React.MouseEvent) => {
    // Don't trigger expand if clicking on buttons
    if ((e.target as HTMLElement).closest("button")) {
      return;
    }
    onToggleExpand?.();
  };

  const handleButtonClick = (e: React.MouseEvent, callback?: () => void) => {
    e.stopPropagation();
    callback?.();
  };

  // Format the date/time for compact display
  const formatDateTime = () => {
    // Use the pre-formatted date and time props instead of parsing again
    if (date && time) {
      return `${date} at ${time}`;
    } else if (date) {
      return date;
    } else if (time) {
      return time;
    }

    // Fallback to original date string if no formatted props
    return "Date TBD";
  };

  return (
    <div
      className={`session-list-item ${onToggleExpand ? "session-list-item-clickable" : ""} ${isExpanded ? "session-list-item-expanded" : ""}`}
      onClick={handleItemClick}
    >
      <div className="session-list-item-content">
        {/* Expand/collapse button */}
        {onToggleExpand && (
          <button
            className="session-list-item-toggle"
            onClick={(e) => handleButtonClick(e, onToggleExpand)}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? "▾" : "▸"}
          </button>
        )}

        {/* Main session info in compact format */}
        <div className="session-list-item-info">
          <span className="session-list-item-title">{title}</span>
          <span className="session-list-item-separator">•</span>
          <span className="session-list-item-dm">{dmName || "DM TBD"}</span>
          <span className="session-list-item-separator">•</span>
          <span className="session-list-item-datetime">{formatDateTime()}</span>
          <span className="session-list-item-separator">•</span>
          <span className="session-list-item-location">{location}</span>
          <span className="session-list-item-separator">•</span>
          <span className="session-list-item-players">
            {players.length}/{maxPlayers} players
          </span>
        </div>

        {/* Action buttons */}
        <div className="session-list-item-actions">
          {actionLabel && (
            <button
              className="btn btn-primary session-list-item-action"
              onClick={(e) => handleButtonClick(e, onAction)}
              disabled={actionDisabled}
            >
              {actionLabel}
            </button>
          )}
          {secondaryActionLabel && (
            <button
              className="btn btn-secondary session-list-item-action"
              onClick={(e) => handleButtonClick(e, onSecondaryAction)}
            >
              {secondaryActionLabel}
            </button>
          )}
          {tertiaryActionLabel && (
            <button
              className="btn btn-secondary session-list-item-action"
              onClick={(e) => handleButtonClick(e, onTertiaryAction)}
            >
              {tertiaryActionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionListItem;
